import { useState } from "react";
import { Button, Dropdown } from "antd";
import { CheckOutlined, DownOutlined, LoadingOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { TrackOnClick } from "../../host/TrackOnClick";
import { TeamRoleLabel } from "./TeamRoleLabel";
import type { TeamMember } from "../types";
import styles from "./MemberRoleSelection.module.css";

export interface MemberRoleSelectionProps {
  member: TeamMember;
  /** Viewer's role on the team. Owner / Admin / Editor / Viewer. */
  viewerRole: string | undefined;
  /** Force read-only regardless of permission (used for SCIM-synced rows). */
  disabled?: boolean;
  /** Role-change API call is in flight for this row — swaps the
   *  chevron for a spinner. Matches the original
   *  `MemberRoleSelectionV2.isUpdatingRole` affordance. */
  pending?: boolean;
  onChange: (role: string) => void;
}

// AHA-43204: tightened from the original "admin can edit non-admin" rule.
// General-api accepts viewer/editor mutations from admins, but the only
// option the dropdown ever surfaces for admins is "Editor" (admin option
// is owner-only via canShowAdminOption), so the dropdown was dead UI on
// editor rows and only weakly useful on the rare viewer-role rows (the
// invite flow no longer creates viewers). Matching the invite modal's
// single-option-hide pattern, admins now see read-only role labels
// across the whole table — only owners can change roles.
//
//   - Owner row → always read-only.
//   - Viewer is Owner → editable.
//   - Otherwise → read-only.
function isViewOnly(
  member: TeamMember,
  viewerRole: string | undefined,
  disabled: boolean,
): boolean {
  if (disabled) return true;
  if (member.accountRole === "owner") return true;
  if (viewerRole === "owner") return false;
  return true;
}

// Only owners can grant admin (matches `showAdminOption`).
function canShowAdminOption(viewerRole: string | undefined): boolean {
  return viewerRole === "owner";
}

// AntD Dropdown's `menu` prop wants a config object — not children — so
// each menu item can't be wrapped with TrackOnClick. We render the
// overlay manually via `popupRender` (renamed from `dropdownRender` in
// AntD 6) so every interactive item can carry its own `name` attribute
// (mirrors the directives in MemberRoleSelectionV2.vue: trigger button,
// admin item, editor item).
export function MemberRoleSelection({
  member,
  viewerRole,
  disabled = false,
  pending = false,
  onChange,
}: MemberRoleSelectionProps) {
  const { t } = useTranslation();
  // Controlled open state — needed to dismiss the popup the moment a
  // role is picked. AntD doesn't auto-close on click for popupRender'd
  // overlays (only for the `menu` config prop), and matching the
  // original UX (MemberRoleSelectionV2 sets `showContextMenu = false`
  // on click regardless of whether the role actually changed).
  const [open, setOpen] = useState(false);

  if (isViewOnly(member, viewerRole, disabled)) {
    return (
      <span style={ {padding: "0 8px"} } data-testid="my-team-member-role-view-only-button">
        <TeamRoleLabel role={member.accountRole} />
      </span>
    );
  }

  const currentRole = member.accountRole;
  const adminVisible = canShowAdminOption(viewerRole);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    // Trigger tracking fires on open (not on click) — captures
    // keyboard-open too, and avoids the React.cloneElement issue that
    // would happen if we wrapped the trigger button with TrackOnClick:
    // AntD injects its own onClick on the trigger to toggle the popup,
    // which a clone would clobber.
    if (!next) return;
    if (typeof window === "undefined" || window.parent === window) return;
    try {
      window.parent.postMessage(
        {
          type: "userAction",
          eventName: "click",
          name: "my-team-member-role-trigger-button",
          properties: {},
        },
        "*",
      );
    } catch {
      /* never leak into the open/close handler */
    }
  };

  // Close the popup the moment an item is clicked, regardless of
  // whether the role actually changed (matches original behaviour).
  const pickRole = (role: string) => {
    setOpen(false);
    onChange(role);
  };

  return (
    <Dropdown
      trigger={["click"]}
      open={open}
      onOpenChange={handleOpenChange}
      popupRender={() => (
        <div className={styles.popup}>
          {adminVisible && (
            <TrackOnClick name="my-team-member-role-admin-menu-item">
              <button
                type="button"
                className={styles.menuItem}
                onClick={() => pickRole("admin")}
                data-testid="my-team-member-role-admin-menu-item"
              >
                <CheckOutlined
                  className={styles.checkIcon}
                  data-visible={currentRole === "admin"}
                />
                {t("team.role.admin")}
              </button>
            </TrackOnClick>
          )}
          <TrackOnClick name="my-team-member-role-editor-menu-item">
            <button
              type="button"
              className={styles.menuItem}
              onClick={() => pickRole("editor")}
              data-testid="my-team-member-role-editor-menu-item"
            >
              <CheckOutlined
                className={styles.checkIcon}
                data-visible={currentRole === "editor" || currentRole === "viewer"}
              />
              {t("team.role.member")}
            </button>
          </TrackOnClick>
        </div>
      )}
    >
      {/* `fontWeight: normal` overrides main.css's app-wide
          `.ant-btn { font-weight: bold }`. The role label must match
          the read-only TeamRoleLabel on the Owner row, which doesn't
          live inside a button. See AHA-42787 #1. */}
      <Button
        type="text"
        style={{ padding: "0 8px", fontWeight: "normal" }}
        disabled={pending}
        data-testid="my-team-member-role-trigger-button"
      >
        <TeamRoleLabel role={currentRole} />
        {pending ? (
          <LoadingOutlined spin style={{ marginLeft: 4, fontSize: 12 }} />
        ) : (
          <DownOutlined style={{ marginLeft: 4, fontSize: 10 }} />
        )}
      </Button>
    </Dropdown>
  );
}
